import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

const NotificationsPanel = () => {
  const { user } = useAuth();
  const { 
    notifications, 
    unreadCount, 
    isPanelOpen, 
    closePanel, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    refreshNotifications 
  } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('unread'); // all, unread, read

  useEffect(() => {
    if (isPanelOpen) {
      refreshNotifications();
    }
  }, [isPanelOpen, filter]);

  // Filtrar notificaciones según el filtro seleccionado
  const filteredNotifications = Array.isArray(notifications) ? notifications.filter(notification => {
    if (filter === 'unread') return !notification.leida;
    if (filter === 'read') return notification.leida;
    return true; // 'all'
  }) : [];

  const abrirPapelera = () => {
    window.open('/papelera', '_blank', 'noopener,noreferrer');
  };

  const getNotificationIcon = (tipo) => {
    const icons = {
      ASIGNACION: '👤',
      ASIGNACION_PARTE: '👤',
      COMPLETACION: '✅',
      PARTE_COMPLETADA: '✅',
      VENCIMIENTO: '⚠️',
      FECHA_LIMITE: '⚠️',
      SISTEMA: '🔔',
      ARCHIVO: '📁',
      ARCHIVO_ELIMINADO: '🗑️',
      EXPEDIENTE: '📋'
    };
    return icons[tipo] || '🔔';
  };

  const getNotificationColor = (tipo) => {
    const colors = {
      ASIGNACION: 'primary',
      ASIGNACION_PARTE: 'primary',
      COMPLETACION: 'success',
      PARTE_COMPLETADA: 'success',
      VENCIMIENTO: 'warning',
      FECHA_LIMITE: 'warning',
      SISTEMA: 'info',
      ARCHIVO: 'secondary',
      ARCHIVO_ELIMINADO: 'danger',
      EXPEDIENTE: 'primary'
    };
    return colors[tipo] || 'secondary';
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    
    const now = new Date();
    const date = new Date(dateString);
    
    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) {
      return 'Fecha no disponible';
    }
    
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora mismo';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Hace ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Hace ${diffInDays}d`;
    
    return date.toLocaleDateString();
  };

  if (!isPanelOpen) return null;

  return (
    <div className="notifications-overlay" onClick={closePanel}>
      <div className="notifications-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="notifications-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              🔔 Notificaciones
              {unreadCount > 0 && (
                <span className="badge badge-danger ml-2">{unreadCount}</span>
              )}
            </h5>
            <div>
              {unreadCount > 0 && (
                <button
                  className="btn btn-sm btn-outline-primary mr-2"
                  onClick={markAllAsRead}
                >
                  Marcar todas como leídas
                </button>
              )}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={closePanel}
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="notifications-filters">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('all')}
            >
              Todas
            </button>
            <button
              type="button"
              className={`btn ${filter === 'unread' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('unread')}
            >
              No leídas
              {unreadCount > 0 && (
                <span className="badge badge-light ml-1">{unreadCount}</span>
              )}
            </button>
            <button
              type="button"
              className={`btn ${filter === 'read' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFilter('read')}
            >
              Leídas
            </button>
          </div>
        </div>

        {/* Lista de notificaciones */}
        <div className="notifications-list">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="sr-only">Cargando...</span>
              </div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <div className="mb-2">🔕</div>
              <p className="mb-0">
                {filter === 'all' ? 'No hay notificaciones' :
                 filter === 'unread' ? 'No hay notificaciones sin leer' :
                 'No hay notificaciones leídas'}
              </p>
            </div>
          ) : (
            filteredNotifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-item ${!notification.leida ? 'unread' : ''}`}
              >
                <div className="notification-content">
                  <div className="notification-icon">
                    <span className={`badge badge-${getNotificationColor(notification.tipo)}`}>
                      {getNotificationIcon(notification.tipo)}
                    </span>
                  </div>
                  <div className="notification-body">
                    <div className="notification-title">
                      {notification.titulo}
                    </div>
                    <div className="notification-message">
                      {notification.mensaje}
                    </div>
                    <div className="notification-meta">
                      <small className="text-muted">
                        {formatTimeAgo(notification.createdAt)}
                      </small>
                    </div>
                    {notification.tipo === 'ARCHIVO_ELIMINADO' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          abrirPapelera();
                        }}
                      >
                        Ver papelera (nueva pestaña)
                      </button>
                    )}
                  </div>
                  <div className="notification-actions">
                    {!notification.leida && (
                      <button
                        className="btn btn-sm btn-outline-primary mr-1"
                        onClick={() => markAsRead(notification.id)}
                        title="Marcar como leída"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => deleteNotification(notification.id)}
                      title="Eliminar"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="notifications-footer">
                  <small className="text-muted">
          {filteredNotifications.length} notificación{filteredNotifications.length !== 1 ? 'es' : ''}
          {unreadCount > 0 && ` • ${unreadCount} sin leer`}
        </small>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPanel;
